'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Home, ChevronLeft } from 'lucide-react';
import { BRAND_BLUE } from '@voley/shared-types/constants';

export default function NotFound() {
  const [isDark, setIsDark] = useState(false);
  
  useEffect(() => {
    // Check for dark mode
    const checkDarkMode = () => {
      const hasDarkClass = document.documentElement.classList.contains('dark-mode');
      const hasDarkAttribute = document.documentElement.getAttribute('data-theme') === 'dark';
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      
      setIsDark(hasDarkClass || hasDarkAttribute || prefersDark);
    };
    
    checkDarkMode();
    
    // Watch for theme changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme']
    });
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => checkDarkMode();
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }
    
    return () => {
      observer.disconnect();
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);
  
  // Brand colors
  const primaryColor = BRAND_BLUE;
  
  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 transition-colors duration-200"
      style={{
        backgroundColor: isDark ? '#0F172A' : '#F8FAFB',
        color: isDark ? '#F1F5F9' : '#374151',
      }}
    >
      <div className="max-w-2xl w-full text-center">
        {/* 404 Number */}
        <div className="mb-8">
          <h1 
            className="text-9xl sm:text-[12rem] font-bold leading-none mb-4 transition-colors"
            style={{
              color: isDark ? '#FFFFFF' : primaryColor,
            }}
          >
            404
          </h1>
        </div>

        {/* Error Message */}
        <div className="mb-12">
          <h2 
            className="text-3xl sm:text-4xl font-semibold mb-4 transition-colors"
            style={{ color: isDark ? '#F1F5F9' : '#111827' }}
          >
            Page Not Found
          </h2>
          <p 
            className="text-lg sm:text-xl mb-2 transition-colors"
            style={{ color: isDark ? '#CBD5E1' : '#6B7280' }}
          >
            The page you&apos;re looking for doesn&apos;t exist or has been moved.
          </p>
          <p 
            className="text-base sm:text-lg transition-colors"
            style={{ color: isDark ? '#94A3B8' : '#9CA3AF' }}
          >
            Let&apos;s get you back on track.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            href="/home"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-base font-medium text-white transition-all hover:transform hover:scale-[1.02] hover:shadow-lg"
            style={{ backgroundColor: primaryColor }}
          >
            <Home className="w-5 h-5" />
            Go Home
          </Link>
          
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-base font-medium transition-all hover:transform hover:scale-[1.02] hover:shadow-md border-2"
            style={{
              backgroundColor: isDark ? '#1E293B' : '#FFFFFF',
              borderColor: isDark ? '#334155' : '#E5E7EB',
              color: isDark ? '#F1F5F9' : '#374151',
            }}
          >
            <ChevronLeft className="w-5 h-5" />
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}

